import { useEffect, useState, useCallback } from 'react';
import { cachedFetch } from '../../utils/api';
import { getFetchMeta } from '../../utils/fetchWithCache';
import { fngLabel } from '../../utils/format';
import { fmtPct } from '../../utils/fmt';
import InfoTooltip from '../InfoTooltip';
import HomeModule, { CoinGeckoMark } from './HomeModule';
import { altszezonIndex, ALTSZEZON_INFO } from '../../utils/altseason';

/* Piaci állapot (3a): Fear & Greed sáv + Altseason + piaci helyzet egy kártyában.
   A négytényezős állapotmodell a 3b-ben jön; itt csak összevonás. */

const FNG_URL = 'https://api.alternative.me/fng/?limit=31';
const FNG_TTL = 60 * 60 * 1000;

function fgColor(v) {
  if (v >= 75) return 'var(--positive)';
  if (v >= 55) return '#66bb6a';
  if (v >= 45) return '#f5a623';
  if (v >= 25) return '#ff7043';
  return 'var(--negative)';
}

function Spark({ values, color }) {
  if (!values || values.length < 2) return <svg className="ms-spark" aria-hidden="true" />;
  const W = 96, H = 26;
  const pts = values.map((v, i) => `${(i / (values.length - 1) * W).toFixed(1)},${(H - v / 100 * H).toFixed(1)}`).join(' ');
  return (
    <svg className="ms-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Az elmúlt 30 nap">
      <line x1="0" x2={W} y1={H / 2} y2={H / 2} stroke="var(--border2)" strokeWidth="1" strokeDasharray="2 3" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

const FG_INFO = 'A Fear & Greed index 0 (extrém félelem) és 100 (extrém mohóság) között mutatja a piaci hangulatot (volatilitás, forgalom, közösségi média, dominancia, trendek alapján). Szélsőséges értékek gyakran fordulópontok közelében jelennek meg.';
const REGIME_INFO = 'Egyszerű összegzés a Fear & Greed értékéből és a BTC 7 napos változásából. A részletesebb, négytényezős állapotmodell később jön.';

export default function MarketState({ coins, fearGreedData, user, marketsMeta }) {
  const [fg, setFg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    cachedFetch(FNG_URL, FNG_TTL)
      .then(d => { if (d?.data) { setFg(d.data); setMeta(getFetchMeta(FNG_URL)); } })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const data = fg ?? fearGreedData;
  const v = data?.[0] ? parseInt(data[0].value, 10) : null;
  const wk = data?.[7] ? parseInt(data[7].value, 10) : null;
  const series = (fg || []).slice(0, 30).map(d => parseInt(d.value, 10)).reverse();
  const color = v != null ? fgColor(v) : 'var(--text3)';

  // Altszezon-index: a közös számítás (utils/altseason.js), ugyanaz, mint az RSI-áttekintésben
  const { score: altScore, beating, total: altTotal, btc7d } = altszezonIndex(coins);
  const altLabel = altScore == null ? '' : altScore >= 75 ? 'Altszezon' : altScore <= 25 ? 'BTC-szezon' : 'Vegyes';
  const altColor = altScore == null ? 'var(--text3)' : altScore >= 75 ? 'var(--positive)' : altScore <= 25 ? 'var(--warn)' : 'var(--blue)';

  // Piaci helyzet
  const fgv = v ?? 50, b7 = btc7d ?? 0;
  const regime = fgv > 70 && b7 > 5 ? ['🚀', 'Bikapiac']
    : fgv > 50 && b7 > 0 ? ['📈', 'Emelkedő trend']
    : fgv < 30 && b7 < -5 ? ['🔴', 'Medvepiac']
    : fgv < 50 && b7 < 0 ? ['📉', 'Csökkenő trend'] : ['↔', 'Oldalazás'];

  // Alternative.me: „the attribution is given right next to the display of the data”
  const fgSource = (
    <a className="hm-attr" href="https://alternative.me/crypto/fear-and-greed-index/" target="_blank" rel="noopener noreferrer" title="Fear & Greed Index: Alternative.me">Alternative.me</a>
  );

  return (
    <HomeModule
      id="market_state" title="Piaci állapot" user={user}
      source="Alternative.me (Fear & Greed) · CoinGecko (altseason, BTC)"
      at={meta?.at ?? marketsMeta?.at} expectedMs={FNG_TTL}
      stale={meta?.stale} error={failed && !data} errorText="Az Alternative.me most nem elérhető" onRetry={load}
      className="ms"
    >
      {/* Fear & Greed */}
      <div className="ms-row">
        <div className="ms-label">Félelem &amp; Mohóság <InfoTooltip text={FG_INFO} /></div>
        <div className="ms-fg">
          <span className="ms-big num" style={{ color }}>{v ?? '—'}</span>
          <span className="ms-fg-txt">
            <span className="ms-fg-cls" style={{ color }}>{v != null ? fngLabel(data[0].value_classification) : '…'}</span>
            <span className="ms-fg-chg num">{wk != null && v != null ? `1 hete ${wk} → ma ${v}` : ''}</span>
          </span>
          <Spark values={series} color={color} />
        </div>
        <div className="ms-bar" role="img" aria-label={`Fear & Greed: ${v ?? 'nincs adat'} / 100`}>
          <div className="ms-bar-track" />
          {[25, 50, 75].map(t => <span key={t} className="ms-tick" style={{ left: `${t}%` }}><i>{t}</i></span>)}
          {v != null && <span className="ms-marker" style={{ left: `${v}%`, background: color }} />}
        </div>
        <div className="hm-attr-row">{fgSource}</div>
      </div>

      {/* Altseason */}
      <div className="ms-row">
        <div className="ms-label">Altszezon-index <InfoTooltip text={ALTSZEZON_INFO} /></div>
        <div className="ms-alt">
          <span className="ms-mid num" style={{ color: altColor }}>{altScore ?? '—'}</span>
          <span className="ms-alt-sub">/ 100 · <span style={{ color: altColor }}>{altLabel}</span></span>
          <span className="ms-alt-meta num">{altScore != null ? `${beating}/${altTotal} alt jobb a BTC-nél (7 nap)` : ''}</span>
        </div>
        <div className="ms-altbar"><div style={{ width: `${altScore ?? 0}%`, background: altColor }} /></div>
        <div className="hm-attr-row"><CoinGeckoMark /></div>
      </div>

      {/* Piaci helyzet */}
      <div className="ms-row ms-regime">
        <div className="ms-label">Piaci helyzet <InfoTooltip text={REGIME_INFO} /></div>
        <div className="ms-reg">
          <span className="ms-reg-ic" aria-hidden="true">{regime[0]}</span>
          <span className="ms-reg-lbl">{regime[1]}</span>
          <span className="ms-reg-sub num">F&amp;G {v ?? '—'} · BTC 7 nap {btc7d != null ? fmtPct(btc7d) : '—'}</span>
        </div>
      </div>
    </HomeModule>
  );
}
